/**
 * Hero-only accessibility enhancements for the shared slideshow component.
 *
 * The shared component owns scrolling, dots, pause/play controls and touch
 * gestures. This wrapper adds keyboard navigation, focus pausing and a strict
 * reduced-motion autoplay opt-out without changing other theme slideshows.
 */
class IwobblHeroCarousel extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {MediaQueryList | null} */
  #reducedMotion = null;

  /** @type {HTMLElement & { next?: Function, previous?: Function, play?: Function, pause?: Function, suspend?: Function, resume?: Function } | null} */
  #carousel = null;

  connectedCallback() {
    customElements.whenDefined('slideshow-component').then(() => {
      if (!this.isConnected) return;
      this.#setup();
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#reducedMotion?.removeEventListener('change', this.#handleMotionChange);
    this.#reducedMotion = null;
    this.#carousel = null;
  }

  #setup() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.#carousel = this.querySelector('slideshow-component');
    if (!this.#carousel) return;

    const { signal } = this.#abort;
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('click', this.#handleControlClick, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#handleMotionChange);
    this.#syncMotionPreference();
  }

  #handleKeydown = (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const target = event.target;
    if (target instanceof Element && target.closest('a, input, select, textarea')) return;

    event.preventDefault();
    const isRtl = document.documentElement.dir === 'rtl';
    const moveNext = isRtl ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
    moveNext ? this.#carousel?.next?.(event) : this.#carousel?.previous?.(event);
  };

  #handleControlClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.#carousel) return;

    if (target.closest('.icon-pause')) {
      queueMicrotask(() => this.#carousel?.setAttribute('aria-live', 'polite'));
    } else if (target.closest('.icon-play')) {
      queueMicrotask(() => this.#carousel?.setAttribute('aria-live', 'off'));
    }
  };

  #handleFocusIn = () => {
    this.#carousel?.suspend?.();
  };

  #handleFocusOut = (event) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && this.contains(nextTarget)) return;
    if (this.#reducedMotion?.matches) return;
    this.#carousel?.resume?.();
  };

  #handleMotionChange = () => {
    this.#syncMotionPreference();
  };

  #syncMotionPreference() {
    if (!this.#carousel || !this.#reducedMotion) return;

    if (this.#reducedMotion.matches) {
      this.#carousel.pause?.();
      this.#carousel.removeAttribute('autoplay');
      this.#carousel.setAttribute('data-reduced-motion', '');
      this.#carousel.setAttribute('aria-live', 'polite');
      return;
    }

    this.#carousel.removeAttribute('data-reduced-motion');

    if (this.dataset.autoplay === 'true') {
      this.#carousel.setAttribute('autoplay', this.dataset.autoplaySpeed || '7');
      this.#carousel.setAttribute('aria-live', 'off');
      this.#carousel.play?.();
    } else {
      this.#carousel.removeAttribute('autoplay');
      this.#carousel.setAttribute('aria-live', 'polite');
    }
  }
}

if (!customElements.get('iwobbl-hero-carousel')) {
  customElements.define('iwobbl-hero-carousel', IwobblHeroCarousel);
}
